import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  pointerWithin,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { Folder, ListMusic, Music2 } from "lucide-react";
import { useBulkPlaylist } from "@web/features/library/bulkApi.ts";
import { useSelectionStore } from "@web/stores/selection.ts";

export const ROOT_DROP_ID = "drop:root";

export type WorkspaceDrag =
  | { kind: "playlist"; id: string; name: string; folderId: string | null }
  | { kind: "folder"; id: string; name: string; parentId: string | null };

export type TracksDrag = {
  kind: "tracks";
  /** Null = filter-mode bulk target (scope/q). */
  trackIds: string[] | null;
  scope?: string;
  q?: string;
  label: string;
  count: number;
};

export type LibraryDrag = WorkspaceDrag | TracksDrag;

export type DropHint = "into" | "before" | "after" | null;

export type DropIntent =
  | { kind: "root" }
  | { kind: "into"; folderId: string }
  | { kind: "before"; parentId: string | null; beforeId: string; targetKind: "folder" | "playlist" }
  | { kind: "after"; parentId: string | null; afterId: string; targetKind: "folder" | "playlist" }
  | { kind: "tracks-onto-playlist"; playlistId: string };

/** In-grid row reorder (not playlist-add). */
export type GridReorderEvent = {
  trackIds: string[];
  overTrackId: string;
  edge: "before" | "after";
};

type WorkspaceHandler = (event: DragEndEvent, pointerY: number) => void;
type GridReorderHandler = (event: GridReorderEvent) => void;

type LibraryDndContextValue = {
  activeDrag: LibraryDrag | null;
  dropHint: DropHint;
  overId: string | null;
  registerWorkspaceHandler: (handler: WorkspaceHandler) => () => void;
  registerGridReorderHandler: (handler: GridReorderHandler) => () => void;
  buildTracksDrag: (trackId: string) => TracksDrag;
};

const LibraryDndCtx = createContext<LibraryDndContextValue | null>(null);

export function useLibraryDnd(): LibraryDndContextValue {
  const ctx = useContext(LibraryDndCtx);
  if (!ctx) throw new Error("useLibraryDnd must be used inside LibraryDndProvider");
  return ctx;
}

/** Safe outside provider (e.g. genres page) — returns null. */
export function useLibraryDndOptional(): LibraryDndContextValue | null {
  return useContext(LibraryDndCtx);
}

export function folderDropId(id: string) {
  return `drop:folder:${id}`;
}

export function folderDragId(id: string) {
  return `drag:folder:${id}`;
}

export function playlistDropId(id: string) {
  return `drop:playlist:${id}`;
}

export function playlistDragId(id: string) {
  return `drag:playlist:${id}`;
}

export function trackDragId(id: string) {
  return `drag:track:${id}`;
}

export function trackDropId(id: string) {
  return `drop:track:${id}`;
}

/**
 * Tracks: only what the pointer is actually over — never closestCenter into a
 * sidebar playlist while still dragging inside the grid.
 * Workspace items: pointerWithin, else closestCenter (tree packing).
 */
const libraryCollision: CollisionDetection = (args) => {
  const dragging = args.active.data.current as LibraryDrag | undefined;
  const hits = pointerWithin(args);
  if (dragging?.kind === "tracks") return hits;
  return hits.length > 0 ? hits : closestCenter(args);
};

export function LibraryDndProvider({ children }: { children: ReactNode }) {
  const [activeDrag, setActiveDrag] = useState<LibraryDrag | null>(null);
  const [dropHint, setDropHint] = useState<DropHint>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const pointerYRef = useRef(0);
  const dropHintRef = useRef<DropHint>(null);
  const lastOverIdRef = useRef<string | null>(null);
  const workspaceHandlerRef = useRef<WorkspaceHandler | null>(null);
  const gridReorderHandlerRef = useRef<GridReorderHandler | null>(null);
  const bulkPlaylist = useBulkPlaylist();
  const isSelected = useSelectionStore((s) => s.isSelected);
  const count = useSelectionStore((s) => s.count);
  const bulkTarget = useSelectionStore((s) => s.bulkTarget);
  const selectedIds = useSelectionStore((s) => s.selectedIds);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );

  const registerWorkspaceHandler = useCallback((handler: WorkspaceHandler) => {
    workspaceHandlerRef.current = handler;
    return () => {
      if (workspaceHandlerRef.current === handler) workspaceHandlerRef.current = null;
    };
  }, []);

  const registerGridReorderHandler = useCallback((handler: GridReorderHandler) => {
    gridReorderHandlerRef.current = handler;
    return () => {
      if (gridReorderHandlerRef.current === handler) gridReorderHandlerRef.current = null;
    };
  }, []);

  const buildTracksDrag = useCallback(
    (trackId: string): TracksDrag => {
      if (isSelected(trackId) && count() > 0) {
        const target = bulkTarget();
        if (target && "entity_ids" in target) {
          return {
            kind: "tracks",
            trackIds: target.entity_ids,
            label: `${target.entity_ids.length} tracks`,
            count: target.entity_ids.length,
          };
        }
        if (target && "scope" in target) {
          return {
            kind: "tracks",
            trackIds: null,
            scope: target.scope,
            q: target.q,
            label: `${count()} tracks`,
            count: count(),
          };
        }
      }
      const ids = selectedIds();
      if (ids && ids.length > 1 && ids.includes(trackId)) {
        return {
          kind: "tracks",
          trackIds: ids,
          label: `${ids.length} tracks`,
          count: ids.length,
        };
      }
      return {
        kind: "tracks",
        trackIds: [trackId],
        label: "1 track",
        count: 1,
      };
    },
    [bulkTarget, count, isSelected, selectedIds],
  );

  const value = useMemo(
    () => ({
      activeDrag,
      dropHint,
      overId,
      registerWorkspaceHandler,
      registerGridReorderHandler,
      buildTracksDrag,
    }),
    [
      activeDrag,
      buildTracksDrag,
      dropHint,
      overId,
      registerGridReorderHandler,
      registerWorkspaceHandler,
    ],
  );

  function setHint(next: DropHint) {
    dropHintRef.current = next;
    setDropHint(next);
  }

  function trackDropEdge(overDropId: string, pointerY: number): "before" | "after" {
    const el = document.querySelector(`[data-track-drop="${overDropId}"]`);
    if (!(el instanceof HTMLElement)) return "after";
    const rect = el.getBoundingClientRect();
    const rel = (pointerY - rect.top) / Math.max(rect.height, 1);
    return rel < 0.5 ? "before" : "after";
  }

  function onDragStart(event: DragStartEvent) {
    const data = event.active.data.current as LibraryDrag | undefined;
    if (data) setActiveDrag(data);
  }

  function onDragMove(event: DragMoveEvent) {
    const translated = event.active.rect.current.translated;
    if (translated) {
      pointerYRef.current = translated.top + translated.height / 2;
    }
    const dragging = event.active.data.current as LibraryDrag | undefined;
    const nextOver =
      typeof event.over?.id === "string"
        ? event.over.id
        : event.over?.id != null
          ? String(event.over.id)
          : null;
    setOverId(nextOver);
    lastOverIdRef.current = nextOver;

    if (!nextOver || nextOver === ROOT_DROP_ID) {
      setHint(null);
      return;
    }

    if (nextOver.startsWith("drop:track:")) {
      if (dragging?.kind !== "tracks" || dragging.trackIds === null) {
        setHint(null);
        return;
      }
      const overTrackId = nextOver.slice("drop:track:".length);
      if (dragging.trackIds.includes(overTrackId)) {
        setHint(null);
        return;
      }
      setHint(trackDropEdge(nextOver, pointerYRef.current));
      return;
    }

    if (nextOver.startsWith("drop:playlist:")) {
      // Tracks → highlight whole row; playlist reorder uses before/after below.
      if (dragging?.kind === "tracks") {
        setHint("into");
        return;
      }
      const el = document.querySelector(`[data-ws-drop="${nextOver}"]`);
      if (!(el instanceof HTMLElement)) {
        setHint("after");
        return;
      }
      const rect = el.getBoundingClientRect();
      const rel = (pointerYRef.current - rect.top) / rect.height;
      setHint(rel < 0.5 ? "before" : "after");
      return;
    }
    if (nextOver.startsWith("drop:folder:")) {
      if (dragging?.kind === "tracks") {
        setHint(null);
        return;
      }
      const el = document.querySelector(`[data-ws-drop="${nextOver}"]`);
      if (!(el instanceof HTMLElement)) {
        setHint("into");
        return;
      }
      const rect = el.getBoundingClientRect();
      const rel = (pointerYRef.current - rect.top) / rect.height;
      if (rel < 0.28) setHint("before");
      else if (rel > 0.72) setHint("after");
      else setHint("into");
      return;
    }
    setHint(null);
  }

  function onDragEnd(event: DragEndEvent) {
    const active = event.active.data.current as LibraryDrag | undefined;
    const eventOverId =
      typeof event.over?.id === "string"
        ? event.over.id
        : event.over?.id != null
          ? String(event.over.id)
          : null;
    // Release often misses `event.over` even when the last move had a target.
    const nextOverId = eventOverId ?? lastOverIdRef.current;
    const translated = event.active.rect.current.translated;
    const pointerY = translated
      ? translated.top + translated.height / 2
      : pointerYRef.current;
    // Prefer live DOM edge over React state — release can race the last setState.
    let hint = dropHintRef.current;
    if (nextOverId?.startsWith("drop:track:")) {
      hint = trackDropEdge(nextOverId, pointerY);
    }
    setActiveDrag(null);
    setHint(null);
    setOverId(null);
    lastOverIdRef.current = null;
    if (!active || nextOverId === null) return;

    if (active.kind === "tracks") {
      if (nextOverId.startsWith("drop:track:")) {
        if (active.trackIds === null || (hint !== "before" && hint !== "after")) return;
        const overTrackId = nextOverId.slice("drop:track:".length);
        if (active.trackIds.includes(overTrackId)) return;
        gridReorderHandlerRef.current?.({
          trackIds: active.trackIds,
          overTrackId,
          edge: hint,
        });
        return;
      }

      if (!nextOverId.startsWith("drop:playlist:")) return;
      const playlistId = nextOverId.slice("drop:playlist:".length);
      if (active.trackIds) {
        bulkPlaylist.mutate({
          local_playlist_id: playlistId,
          action: "add",
          target: { entity_ids: active.trackIds },
        });
      } else if (active.scope) {
        bulkPlaylist.mutate({
          local_playlist_id: playlistId,
          action: "add",
          target: active.q ? { scope: active.scope, q: active.q } : { scope: active.scope },
        });
      }
      return;
    }

    workspaceHandlerRef.current?.(event, pointerY);
  }

  const overlayLabel = (() => {
    if (!activeDrag) return null;
    if (activeDrag.kind === "tracks") {
      if (overId?.startsWith("drop:playlist:")) return `Add ${activeDrag.label} to playlist`;
      if (
        overId?.startsWith("drop:track:") &&
        (dropHint === "before" || dropHint === "after")
      ) {
        return `Reorder ${activeDrag.label}`;
      }
      return activeDrag.label;
    }
    return activeDrag.name;
  })();

  return (
    <LibraryDndCtx.Provider value={value}>
      <DndContext
        sensors={sensors}
        collisionDetection={libraryCollision}
        autoScroll={{
          layoutShiftCompensation: false,
          threshold: { x: 0, y: 0.08 },
          acceleration: 6,
        }}
        onDragStart={onDragStart}
        onDragMove={onDragMove}
        onDragEnd={onDragEnd}
        onDragCancel={() => {
          setActiveDrag(null);
          setHint(null);
          setOverId(null);
          lastOverIdRef.current = null;
        }}
      >
        {children}
        <DragOverlay dropAnimation={null}>
          {activeDrag ? (
            <div className="flex items-center gap-3 rounded-md bg-panel-elevated px-3 py-2 text-sm font-medium shadow-lg ring-1 ring-line">
              {activeDrag.kind === "folder" ? (
                <Folder className="size-4 shrink-0 text-accent" />
              ) : activeDrag.kind === "playlist" ? (
                <ListMusic className="size-4 shrink-0 text-accent" />
              ) : (
                <Music2 className="size-4 shrink-0 text-accent" />
              )}
              <span className="truncate">{overlayLabel}</span>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </LibraryDndCtx.Provider>
  );
}
